import { DateTime } from "luxon";

import { createLogger } from "@/logger";
import { firstOccurrence } from "@/utils/date";

const log = createLogger("reset");

import { getWorkspaceConfig } from "@/data/workspaceConfig";
import { type DeletionResult, deleteItemsAndOrphanedContent } from "@/services/LocalContentDeletion";
import { workspaceId } from "@/store/scope";
import { rowsWhere } from "@/store/store";

type ContentRetentionConfig = { enabled?: boolean; retentionDays?: number };

export function getRetentionConfig(): { enabled: boolean; retentionDays: number } | null {
  if (!workspaceId) return null;
  const config = getWorkspaceConfig(workspaceId) as Record<string, unknown> | undefined;
  const retention = config?.contentRetention as ContentRetentionConfig | undefined;
  if (!retention?.enabled) return null;
  return { enabled: true, retentionDays: retention.retentionDays ?? 180 };
}

export async function applyContentRetention(retentionDays: number): Promise<DeletionResult> {
  // Calendar days back in the local zone, counted as `Date#setDate` counted them: a fractional count truncates the day.
  const now = DateTime.now();
  const cutoffISO = firstOccurrence(now.set({ day: Math.trunc(now.day - retentionDays) }))
    .toJSDate()
    .toISOString();

  log.info(`[applyContentRetention] Starting retention cleanup for items older than ${cutoffISO}`);

  const expiredItems = rowsWhere("item", (item) => item.createdAt < cutoffISO);

  if (expiredItems.length === 0) {
    log.debug("[applyContentRetention] No items to delete");
    return { deletedItems: 0, deletedContentIds: 0, affectedFeeds: 0 };
  }

  const itemIds = expiredItems.map((r) => r.id);
  const feedIds = [...new Set(expiredItems.map((r) => r.feedId))];

  return deleteItemsAndOrphanedContent(itemIds, feedIds, "applyContentRetention");
}
